// Numeric formatting + masking helpers.
//
// Currency formatters (formatMoney, formatPercentage, formatAed) live next
// door in `currencyUtils.ts`. Anything that maps a number to a non-currency
// presentation string belongs here.

const TWO_DECIMAL_PRECISION = 100
const VISIBLE_ACCOUNT_DIGITS = 4

const usDecimalFormatter = new Intl.NumberFormat('en-US')

/**
 * Rounds a number to 2 decimal places. Used by the currency formatters that
 * need to display "AED 1,234.56" cleanly without floating-point artefacts.
 */
export const roundToTwoDecimals = (value: number): number =>
  Math.round(value * TWO_DECIMAL_PRECISION) / TWO_DECIMAL_PRECISION

/**
 * Clamps `value` to the inclusive `[min, max]` range.
 * @example clamp(127, 0, 100) // 100
 * @example clamp(-5, 0, 100)  // 0
 */
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Strips the country code and whitespace from a full mobile number,
 * returning only the national number.
 */
export function toNationalNumber(mobile: string, countryCode?: string | null): string {
  const stripped = countryCode ? mobile.replace(countryCode, '') : mobile
  return stripped.replace(/\s+/g, '').trim()
}

/**
 * Masks a bank account number, revealing only the last 4 digits.
 * @example maskAccountNumber('1234567890') // '•••• 7890'
 */
export function maskAccountNumber(accountNumber?: string | null): string {
  if (!accountNumber) return '••••'
  return `•••• ${accountNumber.slice(-VISIBLE_ACCOUNT_DIGITS)}`
}

/**
 * Formats a number with US locale grouping ("1,234,567"). Returns "0" for
 * non-numeric input. Use `formatMoney` / `formatAed` for currency.
 */
export function formatToInternational(amount: number | string): string {
  const num = typeof amount === 'string' ? Number(amount) : amount
  if (isNaN(num)) return '0'
  return usDecimalFormatter.format(num)
}

const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB

/**
 * Human file size — "344 KB", "1.2 MB". Null for unknown / non-positive
 * sizes so callers can omit the label instead of printing "0 KB".
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null
  if (bytes < BYTES_PER_MB) return `${Math.max(1, Math.round(bytes / BYTES_PER_KB))} KB`
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`
}
